import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { LogOut } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { auth } from '@/lib/auth'
import { useDocumentUpload } from '../../../hooks/use-document-upload'
import {
  deleteConversation,
  fetchActiveConversation,
  type InitialContext,
  type SSEEvent,
  saveUiMessages,
  sendAction,
  sendMessage,
} from '../lib/chat-api'
import { mapUIActionToMessage } from '../lib/sse-event-mapper'
import type { ChatMessage } from '../types'
import { AgentAvatar } from './agent-avatar'
import { ChatInputBar, type ChatInputBarHandle } from './chat-input-bar'
import { DragDropOverlay } from './drag-drop-overlay'
import { MessageBubble } from './message-bubble'
import { TypingIndicator } from './typing-indicator'

const PAUSE_BEFORE_NEXT = 300

const INTERACTION_TOOLS = new Set([
  'request_role_selection',
  'request_identity_input',
  'request_confirmation',
  'request_multi_select',
  'complete_onboarding',
])

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

interface AiOnboardingChatProps {
  logoTargetRef?: React.RefObject<HTMLDivElement | null>
  started?: boolean
  initialContext?: InitialContext
}

export function AiOnboardingChat({
  logoTargetRef,
  started = true,
  initialContext,
}: AiOnboardingChatProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [messages, _setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isTyping, setIsTyping] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const conversationIdRef = useRef<string | null>(null)
  const hasStarted = useRef(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<ChatInputBarHandle>(null)

  const isBusy = isLoading || isTyping || isProcessing

  // Fade logo on scroll
  const [logoOpacity, setLogoOpacity] = useState(1)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    function handleScroll() {
      if (!el) return
      setLogoOpacity(Math.max(0, 1 - el.scrollTop / 80))
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: need to scroll on messages/isTyping changes
  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, scrollToBottom])

  // Ref that always holds the latest messages (updated synchronously inside setMessages)
  const messagesRef = useRef<ChatMessage[]>([])
  const setMessages = useCallback(
    (updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      _setMessages((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        messagesRef.current = next
        return next
      })
    },
    []
  )

  const addMessage = useCallback(
    (msg: ChatMessage) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev
        return [...prev, msg]
      })
    },
    [setMessages]
  )

  const { upload: adHocUpload } = useDocumentUpload({
    onError: (err) => {
      console.error('File upload failed:', err)
      setMessages((prev) => prev.filter((m) => !m.isUploading))
    },
  })

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0]
      if (file) handleAdHocFileUpload(file)
    },
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    disabled: isBusy,
  })

  function getToolSearchingContent(toolName: string): string {
    switch (toolName) {
      case 'scrape_linkedin_profile':
        return 'Fetching LinkedIn profile data...'
      case 'web_search':
        return 'Searching the web...'
      default:
        return 'Working on it...'
    }
  }

  async function processStream(stream: AsyncGenerator<SSEEvent>) {
    setIsProcessing(true)
    setIsTyping(true)
    let textMessageId: string | null = null
    let textCounter = 0

    // Deduplicate searching indicators: one per tool name across the entire stream.
    // Indicators are only resolved when the next non-tool event arrives.
    const searchIndicators = new Map<string, string>()

    function resolveAllIndicators() {
      for (const msgId of searchIndicators.values()) {
        setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, resolved: true } : m)))
      }
      searchIndicators.clear()
    }

    function showError() {
      resolveAllIndicators()
      addMessage({
        id: `error-${Date.now()}`,
        role: 'agent',
        type: 'text',
        content:
          "Sorry, I wasn't able to process that. You can try again or start over if the issue persists.",
      })
    }

    try {
      for await (const event of stream) {
        switch (event.event) {
          case 'text': {
            resolveAllIndicators()
            setIsTyping(false)
            if (!textMessageId) {
              textMessageId = `text-${Date.now()}-${++textCounter}`
              addMessage({ id: textMessageId, role: 'agent', type: 'text', content: '' })
            }
            // Append text token directly to the message as it arrives
            const tokenContent = event.data.content
            const msgId = textMessageId
            setMessages((prev) =>
              prev.map((m) => (m.id === msgId ? { ...m, content: m.content + tokenContent } : m))
            )
            break
          }

          case 'tool_start': {
            // Finalize current text message
            textMessageId = null
            if (!INTERACTION_TOOLS.has(event.data.tool)) {
              if (!searchIndicators.has(event.data.tool)) {
                const indicatorId = `searching-${event.data.id}`
                searchIndicators.set(event.data.tool, indicatorId)
                addMessage({
                  id: indicatorId,
                  role: 'agent',
                  type: 'searching',
                  content: getToolSearchingContent(event.data.tool),
                })
              }
            }
            break
          }

          case 'tool_end': {
            // Don't resolve on tool_end — wait until text/ui_action/done arrives
            break
          }

          case 'ui_action': {
            resolveAllIndicators()
            textMessageId = null

            if (event.data.action === 'complete_onboarding') {
              handleOnboardingComplete(event.data.data)
              break
            }

            const msg = mapUIActionToMessage(event.data)
            if (msg) {
              await pause(PAUSE_BEFORE_NEXT)
              addMessage(msg)
            }
            break
          }

          case 'done': {
            resolveAllIndicators()
            textMessageId = null
            conversationIdRef.current = event.data.conversationId
            // Strip search params after conversation is created
            if (initialContext) {
              navigate({ to: '/join', search: {}, replace: true })
            }
            break
          }

          case 'error': {
            console.error('Chat error:', event.data.message)
            showError()
            break
          }
        }
      }
    } catch (err) {
      console.error('Stream error:', err)
      showError()
    }

    setIsProcessing(false)
    setIsTyping(false)
    persistMessages()
    // Auto-focus the input after the agent finishes responding
    inputRef.current?.focus()
  }

  // Persist UI messages to backend
  function persistMessages() {
    if (conversationIdRef.current && messagesRef.current.length > 0) {
      saveUiMessages(conversationIdRef.current, messagesRef.current)
    }
  }

  async function handleOnboardingComplete(_data: Record<string, unknown>) {
    persistMessages()
    await queryClient.invalidateQueries()
    // AuthGuard handles post-onboarding redirect via consumeAuthRedirect()
    navigate({ to: '/', replace: true })
  }

  // Load existing conversation or kick off a new one
  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger once when started
  useEffect(() => {
    if (!started || hasStarted.current) return
    hasStarted.current = true

    async function init() {
      try {
        const existing = await fetchActiveConversation()
        if (existing && existing.uiMessages.length > 0) {
          conversationIdRef.current = existing.conversationId
          setMessages(existing.uiMessages as ChatMessage[])
          setIsLoading(false)
          return
        }
      } catch {
        // no existing conversation, start fresh
      }
      setIsLoading(false)
      processStream(sendMessage('Hi, I want to get started', undefined, initialContext))
    }

    init()
  }, [started])

  // ─── Handlers ─────────────────────────────────────────────

  function handleChoiceSelect(messageId: string, value: string) {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, resolved: true, selectedValue: value } : m))
    )

    const label = value === 'founder' ? 'Founder' : 'Investor'
    addMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'text',
      content: `I'm a ${label}`,
    })

    persistMessages()

    if (conversationIdRef.current) {
      processStream(
        sendAction(conversationIdRef.current, {
          type: 'role_select',
          data: { selected: label },
        })
      )
    }
  }

  function handleIdentityInput(messageId: string, value: string) {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, resolved: true } : m)))

    addMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'text',
      content: value,
    })

    persistMessages()

    if (conversationIdRef.current) {
      processStream(
        sendAction(conversationIdRef.current, {
          type: 'identity_input',
          data: { value },
        })
      )
    }
  }

  function handleConfirm(
    messageId: string,
    editedSections?: { title: string; items: { label: string; value: string }[] }[]
  ) {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, resolved: true } : m)))

    addMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'text',
      content: 'Confirmed',
    })

    persistMessages()

    if (conversationIdRef.current) {
      processStream(
        sendAction(conversationIdRef.current, {
          type: 'confirm_info',
          data: { confirmed: true, sections: editedSections },
        })
      )
    }
  }

  function handleMultiSelect(messageId: string, values: string[]) {
    const msg = messages.find((m) => m.id === messageId)
    const labels = values.map((v) => msg?.options?.find((o) => o.value === v)?.label ?? v)

    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, resolved: true, selectedValues: values } : m))
    )

    addMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'text',
      content: labels.join(', '),
    })

    persistMessages()

    if (conversationIdRef.current) {
      processStream(
        sendAction(conversationIdRef.current, {
          type: 'multi_select',
          data: { selected: labels },
        })
      )
    }
  }

  function handleAdHocFileUpload(file: File) {
    if (!file.name.toLowerCase().endsWith('.pdf')) return

    const messageId = `upload-${Date.now()}`

    addMessage({
      id: messageId,
      role: 'user',
      type: 'file-upload-status',
      content: `Uploading ${file.name}...`,
      isUploading: true,
    })

    adHocUpload(file)
      .then((documentId) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, content: `Uploaded ${file.name}`, isUploading: false } : m
          )
        )

        if (conversationIdRef.current) {
          processStream(
            sendAction(conversationIdRef.current, {
              type: 'pitch_deck_response',
              data: { documentId },
            })
          )
        }
      })
      .catch(() => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  content: `Failed to upload ${file.name}. Please try again.`,
                  isUploading: false,
                }
              : m
          )
        )
      })
  }

  async function handleStartOver() {
    if (conversationIdRef.current) {
      await deleteConversation(conversationIdRef.current)
    }
    conversationIdRef.current = null
    setMessages([])
    setIsTyping(false)
    setIsProcessing(false)
    processStream(sendMessage('Hi, I want to get started', undefined, initialContext))
  }

  function handleChatSend(text: string) {
    const pendingIdentity = [...messages]
      .reverse()
      .find((m) => m.role === 'agent' && m.type === 'identity-prompt' && !m.resolved)
    if (pendingIdentity) {
      handleIdentityInput(pendingIdentity.id, text)
      return
    }

    addMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'text',
      content: text,
    })

    processStream(sendMessage(text, conversationIdRef.current ?? undefined))
  }

  async function handleSignOut() {
    await auth.signOut()
    window.location.href = '/'
  }

  return (
    <div {...getRootProps()} className="relative flex h-screen flex-col bg-[#141414]">
      <input {...getInputProps()} />
      <DragDropOverlay isDragActive={isDragActive} />

      {/* Sign out button */}
      <button
        type="button"
        onClick={handleSignOut}
        className="absolute right-4 top-4 z-10 flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/5 hover:text-gray-300"
      >
        <LogOut className="size-3.5" />
        Sign out
      </button>

      {/* Messages */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-4xl flex-col gap-4">
          {/* Logo — spacer during animation, real image once chat is active */}
          <div className="flex justify-center pb-6 pt-4">
            {started ? (
              <img
                ref={logoTargetRef as React.RefObject<HTMLImageElement>}
                src="/seedforge-full-light.svg"
                alt="SeedForge"
                className="h-14 pointer-events-none"
                style={{ opacity: logoOpacity }}
              />
            ) : (
              <div ref={logoTargetRef} className="h-14" style={{ aspectRatio: '4.11 / 1' }} />
            )}
          </div>
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              onChoice={handleChoiceSelect}
              onConfirm={handleConfirm}
              onMultiSelect={handleMultiSelect}
            />
          ))}

          {isTyping && (
            <div className="flex gap-3">
              <AgentAvatar />
              <TypingIndicator />
            </div>
          )}

          {messages.length > 0 && !isBusy && (
            <div className="pl-[60px]">
              <Button
                variant="link"
                size="sm"
                onClick={handleStartOver}
                className="text-muted-foreground no-underline hover:text-gray-300"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  role="img"
                >
                  <title>Restart</title>
                  <path d="M3 12a9 9 0 1 1 9 9" />
                  <polyline points="3 4 3 12 11 12" />
                </svg>
                START OVER?
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Input bar pinned to bottom */}
      <div className="shrink-0 border-t border-border px-6 py-6">
        <div className="mx-auto max-w-4xl">
          <ChatInputBar
            ref={inputRef}
            onSend={handleChatSend}
            onFileSelect={handleAdHocFileUpload}
            disabled={isBusy}
          />
        </div>
      </div>
    </div>
  )
}
